// Settings-panel (2026-06-04) — overtar Lyd/App/Innstillinger fra Direksjon-dashboardet (Sven).
import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import {
  APP_VERSION,
  lesCacheTidspunkt,
  sjekkForOppdatering,
} from '@/pwa/registrerSw';
import {
  abonnerInstallTilgjengelig,
  erInstallert,
  triggerInstallPrompt,
} from '@/pwa/installasjon';
import { abonnerAdmin, erAdminAktivert } from '@/state/adminMode';
import { gameState } from '@/state/store';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { lydSlidere } from '@/ui/LydSlidere';
import { Kolonne } from '@/ui/layout';

// Modalen rendres UNDER HUD-scenen (egen scene på toppen), så alt innhold
// må starte under y≈100 for ikke å havne bak HUD-en.
const CONTENT_TOP = 180;
const MASK_BUNN = GAME_HEIGHT - 30;
const PADDING = 40;

export type SettingsModalCallbacks = {
  onAapneAdmin?: () => void;
};

export class SettingsModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private unsubscribeAdmin?: () => void;
  private scrollY = 0;
  private scrollMax = 0;
  private innholdsContainer?: Phaser.GameObjects.Container;
  private mask?: Phaser.Display.Masks.GeometryMask;
  private wheelHandler?: (_p: unknown, _g: unknown, _dx: number, dy: number) => void;
  // Y-koordinat (nullbasert i innhold-containeren) for hvor lyd-slider-
  // overlayet skal sitte. Settes i render, brukes av posisjonerLydSlidere.
  private lydSliderePhaserY = 0;
  private lydSlidereMontert = false;

  constructor(
    private scene: Phaser.Scene,
    private callbacks: SettingsModalCallbacks = {},
  ) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(130);

    // Heldekkende beige bakgrunn over hele skjermen.
    const bg = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.beige, 0.98)
      .setOrigin(0, 0);
    this.layer.add(bg);

    this.wheelHandler = (
      _p: unknown,
      _g: unknown,
      _dx: number,
      dy: number,
    ) => {
      this.scrollBy(dy);
    };
    this.scene.input.on('wheel', this.wheelHandler);

    // Pointer-drag-scroll på bakgrunnen.
    bg.setInteractive();
    bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const startY = pointer.y;
      const startScroll = this.scrollY;
      const onMove = (p: Phaser.Input.Pointer) => {
        const dy = p.y - startY;
        this.scrollY = Math.max(0, Math.min(this.scrollMax, startScroll - dy));
        if (this.innholdsContainer) this.innholdsContainer.y = CONTENT_TOP - this.scrollY;
        if (this.lydSlidereMontert) this.posisjonerLydSlidere();
      };
      const onUp = () => {
        this.scene.input.off('pointermove', onMove);
        this.scene.input.off('pointerup', onUp);
      };
      this.scene.input.on('pointermove', onMove);
      this.scene.input.on('pointerup', onUp);
    });

    this.render();
    this.unsubscribe = gameState.subscribe(() => this.render());
    this.unsubscribeAdmin = abonnerAdmin(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.unsubscribeAdmin?.();
    this.unsubscribeAdmin = undefined;
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    this.layer?.destroy(true);
    this.layer = undefined;
    this.innholdsContainer = undefined;
    this.mask = undefined;
    this.scrollY = 0;
    this.scrollMax = 0;
    if (this.lydSlidereMontert) {
      lydSlidere.destroy();
      this.lydSlidereMontert = false;
    }
  }

  private scrollBy(dy: number): void {
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    if (this.innholdsContainer) this.innholdsContainer.y = CONTENT_TOP - this.scrollY;
    if (this.lydSlidereMontert) this.posisjonerLydSlidere();
  }

  /**
   * Mounter (om nødvendig) DOM-baserte lyd-slidere over Phaser-canvas og
   * oppdaterer posisjonen. Til forskjell fra dashboardet står innhold-
   * containeren her på CONTENT_TOP (ikke 0), så phaserY må kompensere.
   */
  private posisjonerLydSlidere(): void {
    if (!this.layer) {
      if (this.lydSlidereMontert) {
        lydSlidere.destroy();
        this.lydSlidereMontert = false;
      }
      return;
    }
    const innholdsBredde = GAME_WIDTH - PADDING * 2;
    const phaserX = PADDING;
    const phaserY = CONTENT_TOP + this.lydSliderePhaserY - this.scrollY;
    if (!this.lydSlidereMontert) {
      lydSlidere.mount({ x: phaserX, y: phaserY, width: innholdsBredde });
      this.lydSlidereMontert = true;
    } else {
      // Re-mount for å oppdatere posisjon — billig nok.
      lydSlidere.destroy();
      lydSlidere.mount({ x: phaserX, y: phaserY, width: innholdsBredde });
      this.lydSlidereMontert = true;
    }
  }

  private render(): void {
    if (!this.layer) return;
    // Behold kun bakgrunnen (index 0); bygg resten på nytt.
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const innholdsBredde = GAME_WIDTH - PADDING * 2;

    // FAST tittel (ikke scrollet).
    const tittel = this.scene.add
      .text(PADDING, 110, 'Innstillinger', {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    // FAST lukke-knapp øverst til høyre — på linje med tittelen, under
    // HUD-zonen (HUD er egen scene på toppen, så y<100 ville ligge bak den
    // og oppå tannhjul-knappen).
    const lukk = this.scene.add
      .text(GAME_WIDTH - 24, 100, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#862736',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    lukk.on('pointerover', () => lukk.setAlpha(0.7));
    lukk.on('pointerout', () => lukk.setAlpha(1));
    lukk.on('pointerup', () => this.close());
    this.layer.add(lukk);

    // Scrollbar innholds-container (samme mønster som dashboardet).
    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, CONTENT_TOP, GAME_WIDTH, MASK_BUNN - CONTENT_TOP);
    this.mask = maskShape.createGeometryMask();

    const innhold = this.scene.add.container(PADDING, CONTENT_TOP - this.scrollY);
    innhold.setMask(this.mask);
    this.layer.add(innhold);
    this.innholdsContainer = innhold;

    let y = 0;

    y = this.renderLydSeksjon(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderAppInfoSeksjon(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderResetSeksjon(innhold, innholdsBredde, y);

    this.scrollMax = Math.max(0, (CONTENT_TOP + y) - MASK_BUNN);
  }

  // --- Lyd (portert fra DireksjonDashboard) ---

  private renderLydSeksjon(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const overskrift = this.scene.add
      .text(0, y, 'Lyd', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 50;

    const beskrivelse = this.scene.add
      .text(
        0,
        y,
        'Juster volum for musikk, lydeffekter og rom-ambient. Master-mute (lyd-ikonet i HUD) overstyrer alt.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#3a2e22',
          wordWrap: { width },
        },
      )
      .setOrigin(0, 0);
    layer.add(beskrivelse);
    y += beskrivelse.height + 16;

    // DOM-lydslider-overlayet (musikk/lydeffekter/romlyd + voice-over) har
    // faste pikselstørrelser som ikke skalerer med canvas. Vi monterer det
    // først og reserverer så den FAKTISK målte høyden (konvertert til Phaser-
    // koordinater) i stedet for et gjettet tall — ellers la overlayet seg
    // oppå App-seksjonen under. Voice-over ligger som egen rad i overlayet,
    // så vi tegner ikke en egen Phaser-toggle (det var en duplikat).
    this.lydSliderePhaserY = y;
    this.posisjonerLydSlidere();
    const maalt = lydSlidere.maaltHoydePhaser();
    const sliderHoyde = maalt > 0 ? maalt : 360; // fallback hvis ikke målbar
    y += sliderHoyde + 24;

    return y;
  }

  // --- App (portert fra DireksjonDashboard) ---

  private renderAppInfoSeksjon(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const overskrift = this.scene.add
      .text(0, y, 'App', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 50;

    // App-info-linjer stables med Kolonne (konsistent med kodebasen).
    const info = this.scene.add
      .text(0, 0, this.formatterVersjonslinje(), {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
      })
      .setOrigin(0, 0);

    const kreditt = this.scene.add
      .text(0, 0, 'Laget av Indieweb · indieweb.no', {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: '#862736',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    kreditt.on('pointerup', () => {
      if (typeof window !== 'undefined') {
        window.open('https://indieweb.no', '_blank', 'noopener');
      }
    });

    const infoKol = new Kolonne(0, y, 16);
    infoKol.legg(info);
    infoKol.legg(kreditt);
    layer.add([info, kreditt]);
    y = infoKol.bunn() + 16;

    if (!erInstallert()) {
      y = this.renderInstallKnapp(layer, width, y);
      y += 12;
    }

    y = this.renderOppdaterKnapp(layer, width, y);
    y += 12;
    y = this.renderFeedbackKnapp(layer, width, y);

    if (erAdminAktivert()) {
      y += 12;
      y = this.renderAdminKnapp(layer, width, y);
    }
    return y;
  }

  private renderAdminKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 56;
    const bg = lagRundetRekt(this.scene, 0, y, width, knappH, COLORS.wine, 1, 8).setStrokeStyle(
      2,
      COLORS.wine,
      1,
    );
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, '⚙  Admin-modus', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setAlpha(0.85));
    bg.on('pointerout', () => bg.setAlpha(1));
    bg.on('pointerup', () => this.callbacks.onAapneAdmin?.());

    return y + knappH;
  }

  private renderFeedbackKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 56;
    const bg = lagRundetRekt(this.scene, 0, y, width, knappH, COLORS.beige, 1, 8).setStrokeStyle(
      2,
      COLORS.wine,
      0.6,
    );
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, '📧  Send tilbakemelding', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#862736',
        fontStyle: '600',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, 0.4));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.beige, 1));
    bg.on('pointerup', () => {
      if (typeof window === 'undefined') return;
      const emne = encodeURIComponent('Forlagshuset — tilbakemelding');
      const kropp = encodeURIComponent(
        'Hei Sven,\n\nJeg testet Forlagshuset og vil dele:\n\nDet jeg likte:\n- \n\nDet som forvirret meg:\n- \n\nForslag:\n- \n\nMvh,\n',
      );
      window.location.href = `mailto:sven.inge@forfatterskolen.no?subject=${emne}&body=${kropp}`;
    });

    return y + knappH;
  }

  private renderInstallKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 56;
    const bg = lagRundetRekt(this.scene, 0, y, width, knappH, COLORS.ochre, 1, 8).setStrokeStyle(
      2,
      COLORS.wine,
      0.6,
    );
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, '↓  Installer Forlagshuset', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0xc88f3a, 1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.ochre, 1));
    bg.on('pointerup', () => {
      void triggerInstallPrompt();
    });

    const unsub = abonnerInstallTilgjengelig((tilgjengelig) => {
      bg.setAlpha(tilgjengelig ? 1 : 0.45);
      tekst.setAlpha(tilgjengelig ? 1 : 0.6);
      if (tilgjengelig) bg.setInteractive({ useHandCursor: true });
      else bg.disableInteractive();
    });
    bg.once('destroy', () => unsub());

    return y + knappH;
  }

  private renderOppdaterKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 48;
    const bg = lagRundetRekt(this.scene, 0, y, width, knappH, COLORS.white, 1, 8).setStrokeStyle(
      1,
      COLORS.oakDark,
      0.5,
    );
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, 'Sjekk for oppdatering', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#3a2e22',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0xf0e6d0, 1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
    bg.on('pointerup', async () => {
      tekst.setText('Sjekker …');
      const ok = await sjekkForOppdatering();
      tekst.setText(ok ? 'Sjekket — last siden på nytt for ny versjon' : 'Ingen service worker aktiv');
    });

    return y + knappH;
  }

  private formatterVersjonslinje(): string {
    const cacheTid = lesCacheTidspunkt();
    if (!cacheTid) return `Forlagshuset v${APP_VERSION} — cache: ikke etablert`;
    const dd = String(cacheTid.getDate()).padStart(2, '0');
    const mm = String(cacheTid.getMonth() + 1).padStart(2, '0');
    const yyyy = cacheTid.getFullYear();
    return `Forlagshuset v${APP_VERSION} — cache oppdatert ${dd}.${mm}.${yyyy}`;
  }

  // --- Innstillinger / reset (portert fra DireksjonDashboard) ---

  private renderResetSeksjon(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const overskrift = this.scene.add
      .text(0, y, 'Innstillinger', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 50;

    const knappH = 64;
    const bg = lagRundetRekt(this.scene, 0, y, width, knappH, COLORS.white, 1, 8).setStrokeStyle(
      2,
      COLORS.wine,
      0.8,
    );
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, 'Start på nytt (slett save)', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(24),
        color: '#862736',
        fontStyle: '600',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.wine, 0.1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
    bg.on('pointerup', () => this.bekreftReset());

    return y + knappH;
  }

  private bekreftReset(): void {
    if (!this.layer) return;

    const overlay = this.scene.add.container(0, 0).setDepth(140);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    overlay.add(backdrop);

    const dialogW = 520;
    const dialogH = 280;
    const dialogX = (GAME_WIDTH - dialogW) / 2;
    const dialogY = (GAME_HEIGHT - dialogH) / 2;

    const dialog = lagRundetRekt(
      this.scene,
      dialogX,
      dialogY,
      dialogW,
      dialogH,
      COLORS.white,
      1,
      14,
    ).setStrokeStyle(3, COLORS.wine, 1);
    overlay.add(dialog);

    const tittel = this.scene.add
      .text(dialogX + dialogW / 2, dialogY + 40, 'Slett save og start nytt spill?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(26),
        color: '#1a1a1a',
        wordWrap: { width: dialogW - 60 },
        align: 'center',
      })
      .setOrigin(0.5, 0);
    overlay.add(tittel);

    const info = this.scene.add
      .text(
        dialogX + dialogW / 2,
        dialogY + 120,
        'Alle forfattere, bøker, kapital og fremgang blir borte.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#6b6b6b',
          wordWrap: { width: dialogW - 60 },
          align: 'center',
        },
      )
      .setOrigin(0.5, 0);
    overlay.add(info);

    const knappH = 56;
    const knappBredde = 200;
    const knappY = dialogY + dialogH - knappH - 24;

    const avbrytBg = lagRundetRekt(
      this.scene,
      dialogX + 30,
      knappY,
      knappBredde,
      knappH,
      COLORS.white,
      1,
      8,
    ).setStrokeStyle(2, COLORS.oakDark, 0.6);
    const avbrytTxt = this.scene.add
      .text(dialogX + 30 + knappBredde / 2, knappY + knappH / 2, 'Avbryt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#1a1a1a',
      })
      .setOrigin(0.5);
    overlay.add(avbrytBg);
    overlay.add(avbrytTxt);
    avbrytBg.setInteractive({ useHandCursor: true });
    avbrytBg.on('pointerup', () => overlay.destroy(true));

    const bekreftX = dialogX + dialogW - knappBredde - 30;
    const bekreftBg = lagRundetRekt(
      this.scene,
      bekreftX,
      knappY,
      knappBredde,
      knappH,
      COLORS.wine,
      1,
      8,
    );
    const bekreftTxt = this.scene.add
      .text(bekreftX + knappBredde / 2, knappY + knappH / 2, 'Ja, slett alt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    overlay.add(bekreftBg);
    overlay.add(bekreftTxt);
    bekreftBg.setInteractive({ useHandCursor: true });
    bekreftBg.on('pointerover', () => bekreftBg.setFillStyle(0x6f1f2c));
    bekreftBg.on('pointerout', () => bekreftBg.setFillStyle(COLORS.wine));
    bekreftBg.on('pointerup', () => {
      overlay.destroy(true);
      gameState.nullstill();
      if (typeof window !== 'undefined') {
        window.setTimeout(() => window.location.reload(), 250);
      }
    });
  }
}
